import React, { useMemo } from 'react';
import {
  Box,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';

import type { EntityData, EntityPiiData, MatchData } from '../../types';
import { MatchDatum } from './MatchDatum';
import { KeyDataCard } from '../../styles';
import { matchStyle } from './styles';
import { useFetchedEntityName } from '~/entities/components/EntityName/hooks';
import type { Entity } from '~/entities/types';

type MatchKeyDataProps = {
  entity: EntityData;
  match: MatchData;
  entityPii: EntityPiiData;
};

export const MatchDetailKeyData = ({ entity, match, entityPii }: MatchKeyDataProps) => {
  const { currentEntityName } = useFetchedEntityName(entity as Entity);
  const { nameStyle, birthStyle } = (() => {
    const newStyles = {
      nameStyle: {},
      birthStyle: {},
    };
    const { match_fields: matchFields } = match;
    for (let i = 0; i < matchFields.length; i += 1) {
      const field = matchFields[i].toLocaleLowerCase();
      if (field.includes('name')) {
        newStyles.nameStyle = matchStyle;
      } else if (field.includes('of birth') && Boolean(entityPii?.dateOfBirth)) {
        newStyles.birthStyle = matchStyle;
      }
    }
    return newStyles;
  })();

  const pii = useMemo(() => ({
    name: currentEntityName || entity?.entity_name || '—',
    dateOfBirth: entityPii?.dateOfBirth || '—',
    primaryAddress: entityPii?.primaryAddress || '—',
  }), [entity, entityPii, currentEntityName]);

  const {
    name: matchName,
    date_of_birth: matchDateOfBirth,
    entity_type: entityType,
    screening_type: screeningType,
  } = match;
  const isSource = screeningType === 'Source';
  const isPerson = entityType === 'person';
  const isBusiness = entityType === 'business';

  return (
    <KeyDataCard id="watchlist-match-detail-entity">
      <Box data-testId="match-detail-key-data" direction="column" justifyContent="flex-start" gap={4}>
        <Box gap={2} justifyContent="flex-start">
          <Icon icon={isPerson ? IconName.Entities : IconName.Business} />

          <Text lineHeight={4} size={3} weight={700}>
            {isSource ? 'Your Customer' : screeningType}
          </Text>
        </Box>

        {isSource && (
          <>
            <MatchDatum label="Name">
              <Text style={nameStyle}>
                {pii.name}
              </Text>
            </MatchDatum>

            <MatchDatum label="Date Of Birth">
              <Text style={birthStyle}>
                {pii.dateOfBirth}
              </Text>
            </MatchDatum>

            <MatchDatum label="Address">
              <Text>
                {pii.primaryAddress}
              </Text>
            </MatchDatum>
          </>
        )}

        {!isSource && isPerson && (
          <>
            <MatchDatum label="Name">
              <Text style={nameStyle}>
                {matchName}
              </Text>
            </MatchDatum>

            <MatchDatum label="Date Of Birth">
              <Text style={birthStyle}>
                {matchDateOfBirth}
              </Text>
            </MatchDatum>
          </>
        )}

        {!isSource && isBusiness && (
          <MatchDatum label="Name">
            <Text style={nameStyle}>
              {matchName}
            </Text>
          </MatchDatum>
        )}
      </Box>
    </KeyDataCard>
  );
};
